import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { useBrand } from '../BrandContext';

function Login() {
  const { login, sessionExpired } = useAuth();
  const navigate = useNavigate();
  const { t, lang, toggleLang } = useLanguage();
  const { isDark, toggle: toggleTheme } = useTheme();
  const brand = useBrand();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = u.role === 'broker' ? '/portal' : u.role === 'sales' ? '/today' : '/executive';
      navigate(dest, { replace: true });
    } catch (err) {
      // Distinguish a real credential failure (401, server sends an error message)
      // from the app just being briefly unreachable (network error / 5xx during a
      // deploy restart) or rate-limited — so "wrong password" isn't shown when the
      // server is simply restarting.
      const status = err.response?.status;
      if (!err.response || status === 502 || status === 503 || status === 504) {
        setError(t('loginServerDown'));
      } else if (status === 429) {
        setError(t('loginTooMany'));
      } else {
        setError(err.response?.data?.error || 'Login failed');
      }
    }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            {brand.brandLogo && <img src={brand.brandLogo} alt={brand.brandName} style={{ height: 48, filter: 'brightness(1.1)' }} onError={(e) => { e.target.style.display = 'none'; }} />}
          </div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 700, marginTop: 12 }}>{brand.brandName}</h1>
          <p className="text-secondary" style={{ fontSize: '0.85rem', marginTop: 4 }}>{t('loginSubtitle')}</p>
        </div>

        {sessionExpired && !error && (
          <div style={{
            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)',
            borderRadius: 10, padding: '10px 14px', color: '#f59e0b', fontSize: '0.85rem',
            marginBottom: 16, textAlign: 'center', fontWeight: 600,
          }}>{lang === 'ar' ? 'انتهت الجلسة، الرجاء تسجيل الدخول مرة أخرى' : 'Your session expired — please sign in again'}</div>
        )}

        {error && (
          <div style={{
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 10, padding: '10px 14px', color: 'var(--error)', fontSize: '0.85rem',
            marginBottom: 16, textAlign: 'center',
          }}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label>{t('loginEmail')}</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder={brand.adminEmail} autoFocus required style={{ direction: 'ltr' }} />
          </div>
          <div className="input-group">
            <label>{t('loginPassword')}</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••" required style={{ direction: 'ltr' }} />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}
            style={{ width: '100%', padding: '12px', fontSize: '0.95rem', marginTop: 8 }}>
            {loading ? '...' : t('loginButton')}
          </button>
        </form>

        <div className="login-footer">
          <button className="lang-toggle" onClick={toggleLang} style={{ border: 'none', padding: '6px 10px', fontSize: '0.78rem' }}>
            {lang === 'en' ? 'العربية' : 'English'}
          </button>
          <button className="theme-toggle" onClick={toggleTheme} style={{ border: 'none', padding: '6px 10px' }}>
            {isDark ? '\u2600\uFE0F' : '\uD83C\uDF19'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;
